/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-22 07:59:58
 **/

import { create } from "zustand/react";
import { persist, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { PersistenceDatabase } from "@/storage";
import { NormalCrypto } from "@/crypto";
import Properties from "./Properties";

/**
 * Represents the state of properties and their metadata within an application.
 *
 * This interface is commonly used to describe the selection state of a property,
 * its hydration status, and additional information stored about various properties.
 *
 * Properties:
 * - `selected`: The identifier of the currently selected property.
 * - `has_hydrated`: A boolean indicating whether the properties have been hydrated successfully.
 * - `properties`: A record storing metadata about properties. The keys represent property identifiers
 *   and the values are objects containing further information about each property.
 */
interface PropertiesStates {
	selected: string;
	has_hydrated: boolean;
	properties: Record<string, Record<string, unknown | Array<unknown>>>;
}

/**
 * Represents a type defining the state management functions for handling hydration and selection states.
 *
 * This type contains methods to update the hydrated state and the selected ID state.
 *
 * Properties:
 * - `set_hydrated(state: boolean): void` - A function to update the hydrated state. Accepts a boolean parameter to set the current hydrated status.
 * - `set_selected_id(state: string): void` - A function to update the selected ID state. Accepts a string parameter to set the current selected ID.
 */
type HydratedState = {
	set_hydrated: (state: boolean) => void;
	set_selected: (state: string) => void;
};

export const GLOBAL_SELECTED_ID = ":global:";
const PropertiesDefaultStates: PropertiesStates = {
	selected: GLOBAL_SELECTED_ID,
	has_hydrated: false,
	properties: { [GLOBAL_SELECTED_ID]: {} }
};

/**
 * `useProperties` is a state management hook utilized to handle the application state related to properties. It leverages persistent storage, immutability, and reactivity features.
 *
 * - The state is defined with default values from `PropertiesDefaultStates` and provides actions to modify the state, such as `set_hydrated` and `set_selected_id`.
 * - The state is persisted using custom storage mechanics, enabling retrieval, saving, and removal of state with optional encryption.
 * - Automatically rehydrates the state from storage upon initialization and sets a hydrated flag when the process is complete.
 *
 * Features:
 * - State persistence with partial storage of specific values.
 * - Encryption support for secure storage.
 * - Integration with a database layer for data management.
 *
 * Methods:
 * - `set_hydrated(state: boolean)`: Updates the hydrated flag in the state.
 * - `set_selected_id(state: string)`: Updates the selected ID in the state.
 *
 * The state is stored using the key `properties` and is partially saved with a configuration to include only specific segments of the state.
 */
const useProperties = create(
	persist(
		subscribeWithSelector(
			immer<PropertiesStates & HydratedState>((set) => ({
				...PropertiesDefaultStates,
				set_hydrated: (state: boolean) => set({ has_hydrated: state }),
				set_selected: (state: string) => set({ selected: state })
			}))
		),
		{
			name: "properties",
			partialize: (state) => ({
				properties: state.properties
			}),
			storage: {
				async getItem(name) {
					if (name.length === 0) return null;
					const result = await (await PersistenceDatabase.instance.db).get("properties", name);
					return Properties.instance.use_encryption ? NormalCrypto.decrypt<unknown>(result) : result;
				},
				async setItem(name, value) {
					if (name.length === 0) return null;
					const _value = Properties.instance.use_encryption ? NormalCrypto.encrypt(value) : value;
					await (await PersistenceDatabase.instance.db).put("properties", _value, name);
				},
				async removeItem(name) {
					if (name.length === 0) return null;
					await (await PersistenceDatabase.instance.db).delete("properties", name);
				}
			},
			onRehydrateStorage: (state) => {
				PersistenceDatabase.instance.initialize().then(() => {});

				return () => {
					state.set_hydrated(true);
				};
			}
		}
	)
);
export default useProperties;
